import { CircularProgress, Grid } from "@mui/material";
import React from "react";
import { AnyObject, Field } from "react-final-form";
import Libelle from "../../../_i18n/fr.json";
import FormButtons from "../../../components/FormButtons";
import FormContainer from "../../../components/FormContainer";
import InputField from "../../../components/input/InputField";
import InputCheckbox from "../../../components/inputCheckbox/InputCheckbox";
import InputRadio from "../../../components/inputRadio/InputRadio";
import Select from "../../../components/select/Select";
import {
  Bien,
  Commune,
  NombrePieces,
  TypeBien,
  TypeVoie,
} from "../../../services/Api/swagger";
import {
  construireNombrePiecesOptions,
  construireTypesBienOptions,
  construireTypeVoieOptions,
  IBienFormData,
} from "./helpers";
import AutoComplete, { OptionType } from "../../../components/AutoComplete";
import ChoixParcoursForm, { ParcoursLocataire } from "./ChoixParcoursForm";

interface Props {
  onSubmit: (
    event?: Partial<
      Pick<React.SyntheticEvent, "preventDefault" | "stopPropagation">
    >
  ) => Promise<AnyObject | undefined> | undefined;
  formValues: IBienFormData;

  typesVoie?: TypeVoie[];
  typesBien?: TypeBien[];
  nombrePieces?: NombrePieces[];
  onChangeBien: any;
  isFetching: boolean;
  onPrecedent(): void;
  isFetchingSearchPostCode?: boolean;
  onChangeCommune?(commune: Commune): void;
  bienFetching?: boolean;
  communes?: OptionType[];
  isModification: boolean;
  canSeeParcoursLocataire?: boolean;
  dataParcoursLocataire?: ParcoursLocataire;
  statutDossier?: string;
  dossierDuplique?: string;
}

function AjouterBienForm({
  onSubmit,
  typesVoie,
  typesBien,
  nombrePieces,
  isFetching,
  onChangeBien,
  onChangeCommune,
  communes,
  formValues,
  isFetchingSearchPostCode,
  bienFetching,
  isModification,
  canSeeParcoursLocataire,
  dataParcoursLocataire,
  statutDossier,
  dossierDuplique,
}: Readonly<Props>) {
  const onChangeReferenceMandat = (value: string) => {
    const { referenceMandat, numeroLot } = formValues;
    if (value && value !== referenceMandat && numeroLot) {
      preRemplirFormulaire(value, numeroLot);
    }
  };

  const onChangeNumeroLot = (value: string) => {
    const { referenceMandat, numeroLot } = formValues;
    if (value && value !== numeroLot && referenceMandat) {
      preRemplirFormulaire(referenceMandat, value);
    }
  };

  const preRemplirFormulaire = (referenceMandat: string, numeroLot: string) => {
    if (referenceMandat && numeroLot) {
      onChangeBien(referenceMandat, numeroLot);
    }
  };
  canSeeParcoursLocataire;

  const typesBienOptions = typesBien && construireTypesBienOptions(typesBien);
  const typesVoiesOptions = typesVoie && construireTypeVoieOptions(typesVoie);
  const nombrePiecesOptions =
    nombrePieces && construireNombrePiecesOptions(nombrePieces);
  return (
    <form onSubmit={onSubmit} noValidate>
      <ChoixParcoursForm
        formValues={formValues}
        isModification={isModification}
        canSeeParcoursLocataire={canSeeParcoursLocataire}
        dataParcoursLocataire={dataParcoursLocataire}
        statutDossier={statutDossier}
        dossierDuplique={dossierDuplique}
      />
      <h3>{Libelle.pageBien.sections.section1}</h3>
      <FormContainer>
        <Grid item xs={3}>
          <Field
            name="referenceMandat"
            component={InputField}
            onChange={onChangeReferenceMandat}
            label="Référence Mandat"
            maxLength={20}
            required
          />
        </Grid>
        <Grid item xs={3}>
          <Field
            name="numeroLot"
            component={InputField}
            onChange={onChangeNumeroLot}
            label="Numéro de lot"
            maxLength={20}
            required
          />
        </Grid>
        {bienFetching && (
          <Grid item>
            <CircularProgress />
          </Grid>
        )}
      </FormContainer>
      <h3>{Libelle.pageBien.sections.section2}</h3>
      <FormContainer>
        <Grid item xs={2}>
          <Field
            name="numero"
            component={InputField}
            label="N° voie"
            maxLength={10}
            type="text"
          />
        </Grid>

        <Grid item xs={3}>
          <Field
            name="typeVoie"
            component={Select}
            options={typesVoiesOptions}
            label="Type de voie"
            required
          />
        </Grid>
        <Grid item xs={3}>
          <Field
            name="adresse"
            component={InputField}
            label="Nom de la voie"
            maxLength={40}
            required
          />
        </Grid>
        <Grid item xs={2}>
          <Field
            name="numeroBatiment"
            component={InputField}
            label="N° du Batiment"
            maxLength={4}
          />
        </Grid>

        <Grid item>
          <Field
            name="codePostalVille"
            required
            component={AutoComplete}
            fetchOptions={onChangeCommune}
            loading={isFetchingSearchPostCode}
            label={"Code postal / ville"}
            getOptions={communes || []}
          />
        </Grid>
      </FormContainer>

      <h3>{Libelle.pageBien.sections.section3}</h3>
      <FormContainer>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Field
              name="typeBien"
              component={Select}
              options={typesBienOptions}
              label="Type de logement"
              required
              type="text"
            />
          </Grid>
          <Grid item xs={2}>
            <Field
              name="superficie"
              component={InputField}
              label="Superficie"
              max={999}
              required={
                formValues.choixParcours === "parcoursCandidatLocataire"
              }
              type="number"
            />
          </Grid>
          <Grid item xs={3}>
            <Field
              name="nombrePieces"
              component={Select}
              options={nombrePiecesOptions}
              label="Nombre de pièces"
              required={
                formValues.choixParcours === "parcoursCandidatLocataire"
              }
              type="text"
            />
          </Grid>
          <Grid item xs={2}>
            <Field
              name="loyerMensuel"
              component={InputField}
              label="Loyer"
              required
              type="number"
              max={9999}
            />
          </Grid>
          <Grid item xs={2}>
            <Field
              name="chargesMensuelles"
              component={InputField}
              label="Charges"
              max={9999}
              required
              type="number"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item>
            <Field
              name="meuble"
              component={InputCheckbox}
              label="Meublé"
              required
            />
          </Grid>

          <Grid item xs={6}>
            <Field
              name="typeResidence"
              component={InputRadio}
              items={[
                { value: "PRINCIPALE", label: "Principale" },
                { value: "SECONDAIRE", label: "Secondaire" },
              ]}
              label="Pour une résidence"
              required
            />
          </Grid>
        </Grid>
      </FormContainer>

      <FormButtons
        // onPrecedent={onPrecedent}
        fetching={isFetching}
        disabledButton={
          formValues.choixParcours === "parcoursCandidatLocataire" &&
          isModification
        }
        canCancel={isModification ? "oui" : undefined}
        isParcoursCandidatLocataire={
          formValues.choixParcours === "parcoursCandidatLocataire"
        }
      />
    </form>
  );
}

export default AjouterBienForm;
